"use client";

import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { Braces, Gauge, Layers, Radio } from "lucide-react";
import { CODE, GradText, SectionHead, SectionShell, Terminal, useBandRowAlign, useWholeRowBlocks } from "./kit";
import { useEnterStagger } from "./kit/motion";
import { APP_URL } from '@/lib/app-url';

/* Syntax-highlighted with the kit's CODE tokens (the JSON terminals' pattern —
   no highlighter dependency). All three samples are the SAME operation so the
   tabs read as one file in three languages, and they are deliberately kept at
   matching line counts: the stacked-grid equalizer below reserves the tallest
   sample's height, so equal lengths ≈ zero dead space AND zero layout jump. */
const KW = CODE.bool; // keywords — amber
const FN = CODE.number; // call targets / methods — blue

const NODE_CODE = (
  <>
    <span className={CODE.comment}>{"// Create a smart link (routing rules need Premium)"}</span>
    {"\n"}
    <span className={KW}>const</span> res = <span className={KW}>await</span>{" "}
    <span className={FN}>fetch</span>(<span className={CODE.string}>&quot;https://api.rii.link/v1/links&quot;</span>, {"{"}
    {"\n"}  <span className={CODE.key}>method</span>: <span className={CODE.string}>&quot;POST&quot;</span>,
    {"\n"}  <span className={CODE.key}>headers</span>: {"{"} <span className={CODE.key}>Authorization</span>:{" "}
    <span className={CODE.string}>&quot;Bearer rii_live_…&quot;</span>,
    {"\n"}             <span className={CODE.string}>&quot;Content-Type&quot;</span>: <span className={CODE.string}>&quot;application/json&quot;</span> {"}"},
    {"\n"}  <span className={CODE.key}>body</span>: <span className={FN}>JSON.stringify</span>({"{"}
    {"\n"}    <span className={CODE.key}>url</span>: <span className={CODE.string}>&quot;https://myshop.com&quot;</span>,
    {"\n"}    <span className={CODE.key}>slug</span>: <span className={CODE.string}>&quot;summer-sale&quot;</span>,
    {"\n"}    <span className={CODE.key}>routing</span>: [{"{"} <span className={CODE.key}>if</span>: {"{"} <span className={CODE.key}>device</span>:{" "}
    <span className={CODE.string}>&quot;ios&quot;</span> {"}"},
    {"\n"}                <span className={CODE.key}>to</span>: <span className={CODE.string}>&quot;https://apps.apple.com/app/id123&quot;</span> {"}"}],
    {"\n"}  {"}"}),
    {"\n"}{"}"});
    {"\n"}
    <span className={CODE.comment}>{"// → 201 Created · rii.link/summer-sale"}</span>
  </>
);

const PYTHON_CODE = (
  <>
    <span className={CODE.comment}># Create a smart link (routing rules need Premium)</span>
    {"\n"}
    <span className={KW}>import</span> requests
    {"\n"}
    {"\n"}r = <span className={FN}>requests.post</span>(<span className={CODE.string}>&quot;https://api.rii.link/v1/links&quot;</span>,
    {"\n"}    <span className={CODE.key}>headers</span>={"{"}<span className={CODE.string}>&quot;Authorization&quot;</span>:{" "}
    <span className={CODE.string}>&quot;Bearer rii_live_…&quot;</span>{"}"},
    {"\n"}    <span className={CODE.key}>json</span>={"{"}
    {"\n"}        <span className={CODE.string}>&quot;url&quot;</span>: <span className={CODE.string}>&quot;https://myshop.com&quot;</span>,
    {"\n"}        <span className={CODE.string}>&quot;slug&quot;</span>: <span className={CODE.string}>&quot;summer-sale&quot;</span>,
    {"\n"}        <span className={CODE.string}>&quot;routing&quot;</span>: [{"{"}<span className={CODE.string}>&quot;if&quot;</span>: {"{"}<span className={CODE.string}>&quot;device&quot;</span>:{" "}
    <span className={CODE.string}>&quot;ios&quot;</span>{"}"},
    {"\n"}                     <span className={CODE.string}>&quot;to&quot;</span>: <span className={CODE.string}>&quot;https://apps.apple.com/app/id123&quot;</span>{"}"}],
    {"\n"}    {"}"})
    {"\n"}
    <span className={CODE.comment}># → 201 Created · rii.link/summer-sale</span>
  </>
);

const CURL_CODE = (
  <>
    <span className={CODE.comment}># Create a smart link (routing rules need Premium)</span>
    {"\n"}<span className={FN}>curl</span> -X <span className={KW}>POST</span> https://api.rii.link/v1/links \
    {"\n"}  -H <span className={CODE.string}>&quot;Authorization: Bearer rii_live_…&quot;</span> \
    {"\n"}  -H <span className={CODE.string}>&quot;Content-Type: application/json&quot;</span> \
    {"\n"}  -d <span className={CODE.string}>&apos;{"{"}</span>
    {"\n"}<span className={CODE.string}>    &quot;url&quot;: &quot;https://myshop.com&quot;,</span>
    {"\n"}<span className={CODE.string}>    &quot;slug&quot;: &quot;summer-sale&quot;,</span>
    {"\n"}<span className={CODE.string}>    &quot;routing&quot;: [{"{"} &quot;if&quot;: {"{"} &quot;device&quot;: &quot;ios&quot; {"}"},</span>
    {"\n"}<span className={CODE.string}>                  &quot;to&quot;: &quot;https://apps.apple.com/app/id123&quot; {"}"}]</span>
    {"\n"}<span className={CODE.string}>  {"}"}&apos;</span>
    {"\n"}
    <span className={CODE.comment}># → 201 Created · rii.link/summer-sale</span>
  </>
);

const LANGS: { id: string; file: string; code: ReactNode }[] = [
  { id: "node", file: "create-link.js", code: NODE_CODE },
  { id: "python", file: "create-link.py", code: PYTHON_CODE },
  { id: "curl", file: "create-link.sh", code: CURL_CODE },
];

const METHOD_TONE: Record<string, string> = {
  GET: "bg-data-blue/[0.12] text-data-blue",
  POST: "bg-data-green/[0.12] text-data-green",
  PATCH: "bg-data-amber/[0.14] text-data-amber",
  DEL: "bg-brand-600/[0.12] text-brand-600",
};

const ENDPOINTS: { method: string; path: string; param?: string; desc: string }[] = [
  { method: "POST", path: "/v1/links", desc: "Create a link" },
  { method: "GET", path: "/v1/links/", param: ":id", desc: "Fetch a link" },
  { method: "PATCH", path: "/v1/links/", param: ":id", desc: "Update routing" },
  { method: "POST", path: "/v1/qr", desc: "Generate a QR" },
  { method: "GET", path: "/v1/links/", param: ":id/stats", desc: "Click & geo data" },
  { method: "DEL", path: "/v1/links/", param: ":id", desc: "Remove a link" },
];

const FACTS: { Icon: typeof Layers; title: string; body: string }[] = [
  { Icon: Layers, title: "Versioned /v1", body: "Breaking changes only ever land behind a new version prefix." },
  { Icon: Braces, title: "Errors you can parse", body: "Every error is JSON with a stable machine-readable code." },
  { Icon: Radio, title: "Live click stream", body: "Human clicks on your API-created links, streamed over SSE." },
  { Icon: Gauge, title: "Honest rate limits", body: "Limits live in response headers, not in surprise 429s." },
];

export default function ApiSection({ facts = false }: { facts?: boolean }) {
  const [lang, setLang] = useState("node");
  const layoutRef = useRef<HTMLDivElement>(null);
  const riseScopeRef = useRef<HTMLDivElement>(null);
  useWholeRowBlocks(layoutRef);
  useBandRowAlign(layoutRef);
  useEnterStagger(riseScopeRef, "[data-rise]");

  return (
    <SectionShell id="ref-api" divider>
      {/* SectionHead self-choreographs — never wrap it in AnimateOnScroll. */}
      <SectionHead
        eyebrow="Developer API"
        title={
          <>
            Drop link superpowers into <GradText>your stack.</GradText>
          </>
        }
        lead="A fast, predictable REST API with a live event stream. Create, route, brand and measure links from anywhere in your software — in a few lines."
      />

      {/* items-stretch + flex-1 rows: both cards always end on the same line —
          the endpoint rows breathe to fill, the terminal's dark surface covers
          any remainder. Left column deliberately the narrower of the two. */}
      <div ref={riseScopeRef}>
        <div ref={layoutRef} data-grid-block className="grid items-stretch gap-[46px] lg:grid-cols-[1fr_1.1fr]">
          <div data-rise className="min-w-0">
            <div className="flex h-full flex-col overflow-hidden rounded-ref-lg border border-line bg-surface shadow-ref-lg">
              {ENDPOINTS.map((ep) => (
                <div
                  key={`${ep.method}-${ep.path}-${ep.desc}`}
                  className="flex flex-1 items-center gap-3 border-b border-line px-[18px] py-[11px] transition hover:bg-surface-2"
                >
                  <span
                    className={`w-[54px] shrink-0 rounded-md py-[3px] text-center font-jetbrains text-[11px] font-bold ${METHOD_TONE[ep.method]}`}
                  >
                    {ep.method}
                  </span>
                  <span className="font-jetbrains text-[13px] text-ink">
                    {ep.path}
                    {ep.param && <span className="text-muted">{ep.param}</span>}
                  </span>
                  <span className="ml-auto text-[12.5px] text-muted">{ep.desc}</span>
                </div>
              ))}
              {/* Footer fills the card to the terminal's height with content
                  instead of dead space. */}
              <div className="flex items-center gap-3 bg-surface-2 px-[18px] py-[13px]">
                <span className="font-jetbrains text-[11.5px] uppercase tracking-[0.08em] text-dim">
                  Plain HTTPS · any language · OpenAPI
                </span>
                <a
                  href={`${APP_URL}/settings/api-keys`}
                  className="group ml-auto inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand-600"
                >
                  Create an API key
                  <span className="transition-transform group-hover:translate-x-0.5">→</span>
                </a>
              </div>
            </div>
          </div>

          <div data-rise className="min-w-0">
            <Terminal
              className="h-full"
              tabs={LANGS.map((l) => ({ id: l.id, label: l.file }))}
              activeTab={lang}
              onTabSelect={setLang}
            >
              {/* All samples stacked in one grid cell — the terminal reserves the
                  tallest sample's height so switching never moves the layout.
                  Samples are length-matched above, so the reserve ≈ no slack. */}
              <div className="grid">
                {LANGS.map((l) => (
                  <pre
                    key={l.id}
                    aria-hidden={l.id !== lang}
                    className={`m-0 whitespace-pre-wrap font-[inherit] [grid-area:1/1] ${
                      l.id === lang ? "" : "invisible"
                    }`}
                  >
                    {l.code}
                  </pre>
                ))}
              </div>
            </Terminal>
          </div>
        </div>

        {facts && (
          <div data-grid-block className="mt-[46px] grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {FACTS.map(({ Icon, title, body }) => (
              <div
                key={title}
                data-rise
                className="rounded-ref-lg border border-line bg-surface p-5 shadow-ref-lg"
              >
                <Icon size={18} className="text-brand-600" aria-hidden="true" />
                <div className="mt-3 text-[15px] font-bold text-ink">{title}</div>
                <p className="mt-1.5 text-[13.5px] leading-[1.55] text-muted">{body}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </SectionShell>
  );
}
